import React from "react";
import { Box, Button, Chip, IconButton, Slider, Stack, Typography } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";

const FPS = 25;
const BAR_HEIGHT = 72;

// Transport + sync timeline: scrub the clip, drag the RPM trace (or nudge by
// frames) until the needle motion matches the footage. videoTime = logTime + offset.
export default function SyncBar({ videoEl, rows, rpmColumn, offset, setOffset, width, isMobile }) {
  const [playing, setPlaying] = React.useState(!videoEl.paused);
  const [time, setTime] = React.useState(videoEl.currentTime);
  const canvasRef = React.useRef(null);
  const traceRef = React.useRef(null);
  const dragRef = React.useRef(null);
  const duration = videoEl.duration || 0;
  const hasTrace = rows && rpmColumn !== -1;

  React.useEffect(() => {
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    videoEl.addEventListener("play", onPlay);
    videoEl.addEventListener("pause", onPause);
    let raf;
    const tick = () => {
      setTime((prev) =>
        Math.abs(prev - videoEl.currentTime) > 0.02 ? videoEl.currentTime : prev
      );
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      videoEl.removeEventListener("play", onPlay);
      videoEl.removeEventListener("pause", onPause);
      cancelAnimationFrame(raf);
    };
  }, [videoEl]);

  const maxRpm = React.useMemo(() => {
    if (!hasTrace) return 1;
    let max = 1;
    for (const r of rows) max = Math.max(max, parseFloat(r[rpmColumn]) || 0);
    return max;
  }, [rows, rpmColumn, hasTrace]);

  // The trace only re-renders when the alignment changes; the playhead is
  // composited over it every frame.
  React.useEffect(() => {
    if (!width) return;
    if (!traceRef.current) traceRef.current = document.createElement("canvas");
    const c = traceRef.current;
    c.width = width;
    c.height = BAR_HEIGHT;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    if (!hasTrace || !duration) return;

    ctx.beginPath();
    let pen = false;
    for (let x = 0; x <= c.width; x++) {
      const logT = (x / c.width) * duration - offset;
      const idx = Math.round(logT * FPS);
      if (idx < 0 || idx >= rows.length) {
        pen = false;
        continue;
      }
      const v = (parseFloat(rows[idx][rpmColumn]) || 0) / maxRpm;
      const y = c.height - 5 - v * (c.height - 14);
      if (pen) ctx.lineTo(x, y);
      else ctx.moveTo(x, y);
      pen = true;
    }
    ctx.strokeStyle = "#e30613";
    ctx.lineWidth = 2;
    ctx.stroke();
  }, [rows, rpmColumn, offset, duration, maxRpm, width, hasTrace]);

  React.useEffect(() => {
    const c = canvasRef.current;
    if (!c || !width) return;
    c.width = width;
    c.height = BAR_HEIGHT;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    if (traceRef.current) ctx.drawImage(traceRef.current, 0, 0);
    if (duration) {
      const px = (time / duration) * c.width;
      ctx.fillStyle = "rgba(255,255,255,0.9)";
      ctx.fillRect(px - 1, 0, 2, c.height);
    }
  });

  const nudge = (d) => setOffset(offset + d);
  const fmtOffset = `${offset >= 0 ? "+" : "−"}${Math.abs(offset).toFixed(2)}s`;

  return (
    <Box sx={{ width, mt: 1.5 }}>
      <Box
        sx={{
          position: "relative",
          borderRadius: 1.5,
          overflow: "hidden",
          bgcolor: "rgba(255,255,255,0.05)",
          border: "1px solid",
          borderColor: "divider",
          cursor: hasTrace ? "grab" : "default",
          touchAction: "none",
          lineHeight: 0,
        }}
        onPointerDown={(e) => {
          if (!hasTrace) return;
          dragRef.current = { x: e.clientX, offset };
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          if (!dragRef.current) return;
          const dx = e.clientX - dragRef.current.x;
          setOffset(dragRef.current.offset + (dx / width) * duration);
        }}
        onPointerUp={() => (dragRef.current = null)}
      >
        <canvas
          ref={canvasRef}
          style={{ width: "100%", height: isMobile ? 92 : BAR_HEIGHT }}
        />
        {!hasTrace && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }}
          >
            Load a log to see the RPM trace
          </Typography>
        )}
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
        rowGap={0.5}
        sx={{ mt: 0.5 }}
      >
        <IconButton
          size={isMobile ? "medium" : "small"}
          onClick={() => (videoEl.paused ? videoEl.play() : videoEl.pause())}
        >
          {playing ? <PauseIcon /> : <PlayArrowIcon />}
        </IconButton>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ minWidth: 86, fontVariantNumeric: "tabular-nums" }}
        >
          {time.toFixed(2)} / {duration.toFixed(2)}s
        </Typography>
        <Slider
          size="small"
          value={Math.min(time, duration)}
          max={duration}
          step={1 / FPS}
          onChange={(_, v) => {
            videoEl.currentTime = v;
            setTime(v);
          }}
          sx={{ flex: "1 1 140px", minWidth: 140, mx: 1 }}
        />
        {hasTrace && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={isMobile ? 0.75 : 0.5}
            useFlexGap
            flexWrap="wrap"
            sx={{
              ml: { xs: 0, sm: "auto" },
              width: { xs: "100%", sm: "auto" },
              justifyContent: { xs: "space-between", sm: "flex-end" },
            }}
          >
            <Chip
              size="small"
              variant="outlined"
              label={`offset ${fmtOffset}`}
              sx={{ fontVariantNumeric: "tabular-nums" }}
            />
            {[
              ["−0.1s", -0.1],
              ["−1f", -1 / FPS],
              ["+1f", 1 / FPS],
              ["+0.1s", 0.1],
            ].map(([label, d]) => (
              <Button
                key={label}
                size={isMobile ? "medium" : "small"}
                variant={isMobile ? "outlined" : "text"}
                color="inherit"
                sx={{
                  minWidth: isMobile ? 52 : 44,
                  px: isMobile ? 1 : 0.5,
                  color: "text.secondary",
                  borderColor: isMobile ? "divider" : "transparent",
                }}
                onClick={() => nudge(d)}
              >
                {label}
              </Button>
            ))}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
